import React, { Component, ReactNode } from 'react';
import { AxiosError } from 'axios';
import { QueryErrorResetBoundary } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: any;
}

/**
 * Error boundary specialized for React Query errors.
 * Extracts axios error information and displays user-friendly messages.
 * Provides retry button that resets React Query error state.
 * Integrates with QueryErrorResetBoundary for proper error recovery.
 */
class QueryErrorBoundaryInner extends Component<Props & { reset: () => void }, State> {
  constructor(props: Props & { reset: () => void }) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: any): State {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: any, errorInfo: React.ErrorInfo) {
    console.error('QueryErrorBoundary caught an error:', error, errorInfo);
  }

  handleRetry = () => {
    // Reset the error boundary state
    this.setState({
      hasError: false,
      error: null,
    });

    // Reset React Query error state
    this.props.reset();
  };

  getErrorMessage(): string {
    const error = this.state.error;

    // Check if it's an axios error
    if (error?.isAxiosError || error?.response) {
      const axiosError = error as AxiosError;
      const status = axiosError.response?.status;

      switch (status) {
        case 400:
          return 'Ungültige Anfrage. Bitte überprüfen Sie Ihre Eingaben.';
        case 403:
          return 'Sie haben keine Berechtigung für diese Aktion.';
        case 404:
          return 'Die angeforderten Daten wurden nicht gefunden.';
        case 500:
          return 'Ein Serverfehler ist aufgetreten. Bitte versuchen Sie es später erneut.';
        case 503:
          return 'Der Server ist vorübergehend nicht verfügbar. Bitte versuchen Sie es später erneut.';
        default:
          if (!axiosError.response && axiosError.request) {
            return 'Keine Verbindung zum Server. Bitte überprüfen Sie Ihre Internetverbindung.';
          }
          return 'Ein Fehler ist beim Laden der Daten aufgetreten.';
      }
    }

    // Generic error message for non-axios errors
    return 'Ein unerwarteter Fehler ist aufgetreten.';
  }

  getErrorDetails(): string | null {
    const error = this.state.error;

    if (error?.isAxiosError || error?.response) {
      const axiosError = error as AxiosError;
      const status = axiosError.response?.status;
      const statusText = axiosError.response?.statusText;
      const url = axiosError.config?.url;

      return `Status: ${status} ${statusText}\nURL: ${url}`;
    }

    if (error?.message) {
      return error.message;
    }

    return null;
  }

  render() {
    if (this.state.hasError) {
      const errorMessage = this.getErrorMessage();
      const errorDetails = this.getErrorDetails();

      return (
        <div className="d-flex align-items-center justify-content-center p-4">
          <div className="card" style={{ maxWidth: '500px', width: '100%' }}>
            <div className="card-body">
              <h5 className="card-title text-warning">Fehler beim Laden</h5>
              <p className="card-text">{errorMessage}</p>

              {errorDetails && (
                <details className="mt-3">
                  <summary className="text-muted" style={{ cursor: 'pointer' }}>
                    Technische Details
                  </summary>
                  <pre
                    className="mt-2 p-2 bg-light border rounded"
                    style={{ fontSize: '0.875rem', overflow: 'auto' }}
                  >
                    {errorDetails}
                  </pre>
                </details>
              )}

              <button className="btn btn-primary mt-3" onClick={this.handleRetry}>
                Erneut versuchen
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Wrapper component that provides QueryErrorResetBoundary context.
 * Use this in your component tree to catch and handle React Query errors.
 *
 * Example:
 * <QueryErrorBoundary>
 *   <Suspense fallback={<Loading />}>
 *     <MyComponent />
 *   </Suspense>
 * </QueryErrorBoundary>
 */
export function QueryErrorBoundary({ children }: Props) {
  const location = useLocation();
  return (
    <QueryErrorResetBoundary>
      {({ reset }) => (
        <QueryErrorBoundaryInner key={location.pathname} reset={reset}>{children}</QueryErrorBoundaryInner>
      )}
    </QueryErrorResetBoundary>
  );
}
